import { Observable, Subscription } from 'rxjs';
import { IMessagesResourceService } from 'app/core/resources/resources.service';
import { AlertService } from '../../shared/alert/alert.service';

/*
 * Shared failure handling for PUT /order-service/:id (CAS-1269, PR 183 review finding C).
 *
 * The update used to answer a failed save with a 200, so callers only ever wrote a success
 * callback. It now fails with an error status (e.g. "Account don't exist" for a BAN with no
 * account, or a duplicate SP circuit id), and a caller with no error callback failed silently:
 * no message, stepper not moving. Every order-service save goes through these helpers so a
 * failure always shows one error alert and runs none of the success-path follow-ons.
 */

/** What a caller needs to save an order service and report a failure. */
export interface OrderServiceSaveHost {
	orderServiceService: { update(id: number, item: any): Observable<any> };
	alertService: AlertService;
	messages: IMessagesResourceService;
}

export const ORDER_SERVICE_SAVE_ERROR_KEY = 'ORDER_SERVICE_SAVE_ERROR';

const usable = (value: any): string | null => {
	if (typeof value !== 'string') {
		return null;
	}
	const text = value.trim();
	// An HTML error page from a proxy (502/504) is not a message to show the user.
	return text && !text.startsWith('<') ? text : null;
};

/**
 * The server's message from a failed save, when it sent one. The app error middleware
 * (apps/server api/3.0/common/error.middleware.js) answers `{ status, message? }`: "Account don't
 * exist" comes as a 422 with its message; a database or engine failure comes as a 500 with no
 * message, so that case falls back to the caller's text. `error.message` (an older `{ error }`
 * body) and a bare string body are still read.
 */
export function orderServiceSaveErrorMessage(error: any): string | null {
	const body = error?.error;
	return usable(body?.error?.message)
		?? usable(body?.message)
		?? usable(body?.error)
		?? usable(body);
}

/** Shows the single error alert for a failed order-service save. */
export function alertOrderServiceSaveError(host: Pick<OrderServiceSaveHost, 'alertService' | 'messages'>, error: any,
	fallbackKey: string = ORDER_SERVICE_SAVE_ERROR_KEY) {
	host.alertService.error('', orderServiceSaveErrorMessage(error) ?? host.messages.get(fallbackKey));
}

/**
 * PUT the order service. `saved` runs only on success; on failure the error alert is shown and
 * `failed` (if given) runs - use it to undo any in-memory change made before the save. Nothing
 * else runs after a failure (no notes, no stepper move, no order refresh).
 */
export function saveOrderService(host: OrderServiceSaveHost, orderService: any,
	saved: (result: any) => void,
	options: { failed?: (error: any) => void, fallbackKey?: string } = {}): Subscription {
	return host.orderServiceService.update(orderService.id, orderService).subscribe({
		next: saved,
		error: (error) => {
			alertOrderServiceSaveError(host, error, options.fallbackKey);
			if (options.failed) {
				options.failed(error);
			}
		}
	});
}
